/**
 * ADMIN-T017 (D12-B) — Pinia permission store. Single source of truth for the
 * current actor's token, role, permission codes, and region/project scope.
 * Runtime state is in-memory; src/auth/session.ts owns the short-term
 * sessionStorage mirror used only for same-tab admin refresh recovery.
 *
 * Requires setActivePinia(pinia) before first use (done in main.ts before bootstrap).
 */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

/** Shape returned by POST /admin-bff/auth/login (D12-A contract). */
export interface LoginEnvelope {
  token: string;
  userId: string;
  userName: string;
  user?: { actorId?: string; displayName?: string | null; role?: string };
  permissions: string[];
  regionScope: string[];
  projectScope: string[];
  permissionVersion?: string;
}

/** Shape returned by GET /admin-bff/me/permissions (D12-A contract). */
export interface MeEnvelope {
  userId: string;
  userName: string;
  role?: string;
  permissions: string[];
  regionScope: string[];
  projectScope: string[];
  permissionVersion?: string;
}

export const usePermissionStore = defineStore('permission', () => {
  const token = ref<string | null>(null);
  const userId = ref<string | null>(null);
  const userName = ref<string | null>(null);
  const role = ref<string | null>(null);
  const permissions = ref<string[]>([]);
  const regionScope = ref<string[]>([]);
  const projectScope = ref<string[]>([]);
  const permissionVersion = ref<string | null>(null);

  const isAuthenticated = computed(() => token.value !== null);

  function hasPermission(code: string): boolean {
    return permissions.value.includes('*') || permissions.value.includes(code);
  }

  function inRegionScope(regionId: string): boolean {
    return regionScope.value.includes('*') || regionScope.value.includes(regionId);
  }

  function inProjectScope(projectId: string): boolean {
    return projectScope.value.includes('*') || projectScope.value.includes(projectId);
  }

  function setFromLogin(env: LoginEnvelope): void {
    token.value = env.token;
    userId.value = env.userId;
    userName.value = env.userName;
    role.value = env.user?.role ?? null;
    permissions.value = env.permissions;
    regionScope.value = env.regionScope;
    projectScope.value = env.projectScope;
    permissionVersion.value = env.permissionVersion ?? null;
  }

  function setFromMe(env: MeEnvelope): void {
    userId.value = env.userId;
    userName.value = env.userName;
    role.value = env.role ?? null;
    permissions.value = env.permissions;
    regionScope.value = env.regionScope;
    projectScope.value = env.projectScope;
    permissionVersion.value = env.permissionVersion ?? null;
  }

  function clear(): void {
    token.value = null;
    userId.value = null;
    userName.value = null;
    role.value = null;
    permissions.value = [];
    regionScope.value = [];
    projectScope.value = [];
    permissionVersion.value = null;
  }

  return {
    token,
    userId,
    userName,
    role,
    permissions,
    regionScope,
    projectScope,
    permissionVersion,
    isAuthenticated,
    hasPermission,
    inRegionScope,
    inProjectScope,
    setFromLogin,
    setFromMe,
    clear,
  };
});
